import { useState } from 'react';
import { Box, Typography, TextField, Button, Stack, Chip, InputAdornment, Tabs, Tab } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Search, MessageSquare, Gift, Star, AlertCircle, CheckCheck, Filter } from 'lucide-react';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F9FAFB'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%'
}));

interface NotificationType {
  id: number;
  type: 'survey' | 'reward' | 'review' | 'alert' | 'system';
  title: string;
  message: string;
  time: string;
  date: string;
  read: boolean;
  icon: any;
  color: string;
}

const Notifications = () => {
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [notifications, setNotifications] = useState<NotificationType[]>([
    {
      id: 1,
      type: 'survey',
      title: 'Survey Completed',
      message: 'John Doe completed the product feedback survey for "Premium Widget"',
      time: '5 minutes ago',
      date: '2024-01-15',
      read: false,
      icon: MessageSquare,
      color: '#4F46E5'
    },
    {
      id: 2,
      type: 'reward',
      title: 'Reward Claimed',
      message: 'Sarah Smith claimed $10 reward for their 5-star review on Google',
      time: '1 hour ago',
      date: '2024-01-15',
      read: false,
      icon: Gift,
      color: '#10B981'
    },
    {
      id: 3,
      type: 'review',
      title: 'New Review Posted',
      message: 'A new 5-star review was posted on Google by Michael Johnson',
      time: '2 hours ago',
      date: '2024-01-15',
      read: false,
      icon: Star,
      color: '#F59E0B'
    },
    {
      id: 4,
      type: 'alert',
      title: 'Negative Review Alert',
      message: 'A 2-star review from Jane Wilson needs your immediate attention',
      time: '3 hours ago',
      date: '2024-01-15',
      read: true,
      icon: AlertCircle,
      color: '#EF4444'
    },
    {
      id: 5,
      type: 'system',
      title: 'Survey Created',
      message: 'New product survey "Summer Collection Feedback" has been created and sent to 150 customers',
      time: '5 hours ago',
      date: '2024-01-15',
      read: true,
      icon: CheckCheck,
      color: '#6366F1'
    },
    {
      id: 6,
      type: 'survey',
      title: 'Survey Completed',
      message: 'Emily Davis completed the service quality survey',
      time: '1 day ago',
      date: '2024-01-14',
      read: true,
      icon: MessageSquare,
      color: '#4F46E5'
    },
    {
      id: 7,
      type: 'reward',
      title: 'Multiple Rewards Pending',
      message: '5 reward claims are awaiting your approval totaling $75',
      time: '1 day ago',
      date: '2024-01-14',
      read: true,
      icon: Gift,
      color: '#10B981'
    },
    {
      id: 8,
      type: 'review',
      title: 'New Review on Facebook',
      message: 'Robert Brown posted a 4-star review on your Facebook page',
      time: '2 days ago',
      date: '2024-01-13',
      read: true,
      icon: Star,
      color: '#F59E0B'
    },
    {
      id: 9,
      type: 'system',
      title: 'Campaign Completed',
      message: 'Your "Winter Sale Feedback" campaign has been completed with 89% response rate',
      time: '3 days ago',
      date: '2024-01-12',
      read: true,
      icon: CheckCheck,
      color: '#6366F1'
    },
    {
      id: 10,
      type: 'alert',
      title: 'Low Response Rate Alert',
      message: 'Your recent survey "Holiday Experience" has a low response rate of 12%',
      time: '3 days ago',
      date: '2024-01-12',
      read: true,
      icon: AlertCircle,
      color: '#EF4444'
    }
  ]);

  const tabs = [
    { label: 'All', value: 'all' },
    { label: 'Surveys', value: 'survey' },
    { label: 'Rewards', value: 'reward' },
    { label: 'Reviews', value: 'review' },
    { label: 'Alerts', value: 'alert' },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleMarkAsRead = (id: number) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, read: true }))
    );
  };

  const filteredNotifications = notifications.filter(notif => {
    const matchesSearch = notif.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         notif.message.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = currentTab === 0 || notif.type === tabs[currentTab].value;
    return matchesSearch && matchesTab;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Page Header */}
          <Box sx={{ mb: 4 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 700,
                    fontSize: { xs: '1.875rem', md: '2.25rem' },
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                    color: '#111827',
                    mb: 0.5
                  }}
                >
                  Notifications
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1rem',
                    color: '#6B7280'
                  }}
                >
                  {unreadCount > 0 
                    ? `You have ${unreadCount} unread ${unreadCount === 1 ? 'notification' : 'notifications'}`
                    : 'All caught up! No unread notifications'
                  }
                </Typography>
              </Box>
              
              {unreadCount > 0 && (
                <Button
                  onClick={handleMarkAllAsRead}
                  sx={{
                    textTransform: 'none',
                    color: '#4F46E5',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: '#EEF2FF'
                    }
                  }}
                >
                  Mark all as read
                </Button>
              )}
            </Stack>
          </Box>

          {/* Search and Filters */}
          <Box sx={{ mb: 4 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#E5E7EB'
                    },
                    '&:hover fieldset': {
                      borderColor: '#D1D5DB'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4F46E5'
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={20} color="#9CA3AF" />
                    </InputAdornment>
                  )
                }}
              />
            </Stack>

            {/* Filter Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: '#E5E7EB' }}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#6B7280',
                    minHeight: 48,
                    '&.Mui-selected': {
                      color: '#4F46E5',
                      fontWeight: 600
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#4F46E5',
                    height: 2
                  }
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab key={index} label={tab.label} />
                ))}
              </Tabs>
            </Box>
          </Box>

          {/* Notifications List */}
          <Stack spacing={2}>
            {filteredNotifications.length === 0 ? (
              <Box
                sx={{
                  bgcolor: 'white',
                  borderRadius: '12px',
                  p: 6,
                  textAlign: 'center',
                  border: '1px solid #E5E7EB'
                }}
              >
                <Typography sx={{ color: '#6B7280', fontSize: '1rem' }}>
                  No notifications found
                </Typography>
              </Box>
            ) : (
              filteredNotifications.map((notification) => {
                const IconComponent = notification.icon;
                return (
                  <Box
                    key={notification.id}
                    sx={{
                      bgcolor: notification.read ? 'white' : '#F0F9FF',
                      borderRadius: '12px',
                      p: 3,
                      border: `1px solid ${notification.read ? '#E5E7EB' : '#BAE6FD'}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                      '&:hover': {
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        borderColor: notification.read ? '#D1D5DB' : '#7DD3FC'
                      }
                    }}
                    onClick={() => !notification.read && handleMarkAsRead(notification.id)}
                  >
                    <Stack direction="row" spacing={3}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          bgcolor: `${notification.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <IconComponent size={24} color={notification.color} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                              <Typography
                                sx={{
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  color: '#111827'
                                }}
                              >
                                {notification.title}
                              </Typography>
                              {!notification.read && (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: '#4F46E5'
                                  }}
                                />
                              )}
                            </Stack>
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                color: '#6B7280',
                                lineHeight: 1.6,
                                mb: 1
                              }}
                            >
                              {notification.message}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: '#9CA3AF'
                              }}
                            >
                              {notification.time}
                            </Typography>
                          </Box>
                          <Chip
                            label={notification.type.charAt(0).toUpperCase() + notification.type.slice(1)}
                            size="small"
                            sx={{
                              bgcolor: `${notification.color}15`,
                              color: notification.color,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 24,
                              borderRadius: '6px'
                            }}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                );
              })
            )}
          </Stack>
        </ContentArea>
      </MainContent>
    </PageContainer>
  );
};

export default Notifications;

